import { BrowserModule, Title } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  BrowserAnimationsModule,
  NoopAnimationsModule,
} from '@angular/platform-browser/animations';
import {
  HttpClientModule,
  HttpClient,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { CountdownModule } from 'ngx-countdown';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NotifierModule, NotifierOptions } from 'angular-notifier';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { LoginModule } from './pages/login';
import { OrdersModule } from './pages/orders';
import { AppMaterialModule } from './app-material.module';
import { OnboardingsModule } from './pages/onboardings';
import { RegisterModule } from './pages/register';
import { AccountModule } from './pages/account';
import { BoobleModalModule } from 'booble-modal';
import { NgxMaskModule } from 'ngx-mask';
import { OnboardingsComponent } from './pages/onboardings/onboardings.component';
import { RegisterLayoutComponent } from './shared/layouts/register-layout/register-layout.component';
import { FooterLayoutComponent } from './shared/layouts/footer-layout/footer-layout.component';
import { OnboardingsLayoutComponent } from './shared/layouts/onboardings-layout/onboardings-layout.component';
import { MainLayoutComponent } from './shared/layouts/main-layout/main-layout.component';
import { LoginLayoutComponent } from './shared/layouts/login-layout/login-layout.component';
import { AuthInterceptor } from './core/interceptors/auth-interceptor';
import { ResponseInterceptor } from './core/interceptors/response-interceptor';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatPaginatorIntlBr } from './shared/components/custom-paginator';
import { NgxCurrencyModule, CurrencyMaskInputMode } from 'ngx-currency';
import { AdminModule } from './pages/admin';
import {
  MAT_MOMENT_DATE_FORMATS,
  MomentDateAdapter,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
} from '@angular/material-moment-adapter';

import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { WebsocketService } from './shared/services/websocket.service';
import { SharedComponentsModule } from './shared/components/shared-components.module';
import { RouterModule } from '@angular/router';
import { SavedContactsModule } from './pages/saved-contacts/saved-contacts.module';

const customNotifierOptions: NotifierOptions = {
  position: {
    horizontal: {
      position: 'right',
      distance: 12,
    },
    vertical: {
      position: 'top',
      distance: 48,
      gap: 10,
    },
  },
  theme: 'material',
  behaviour: {
    autoHide: 15000,
    onClick: 'hide',
    onMouseover: 'pauseAutoHide',
    showDismissButton: true,
    stacking: 4,
  },
  animations: {
    enabled: true,
    show: {
      preset: 'slide',
      speed: 300,
      easing: 'ease',
    },
    hide: {
      preset: 'fade',
      speed: 300,
      easing: 'ease',
      offset: 50,
    },
    shift: {
      speed: 300,
      easing: 'ease',
    },
    overlap: 150,
  },
};

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(
    http,
    './home-broker/prd/app/assets/i18n/',
    '.json'
  );
}
export const customCurrencyMaskConfig = {
  align: 'right',
  allowNegative: true,
  allowZero: true,
  decimal: ',',
  precision: 2,
  prefix: 'R$ ',
  suffix: '',
  thousands: '.',
  nullable: true,
  min: null,
  max: null,
  inputMode: CurrencyMaskInputMode.FINANCIAL,
};

@NgModule({
  declarations: [
    AppComponent,
    OnboardingsComponent,
    RegisterLayoutComponent,
    FooterLayoutComponent,
    OnboardingsLayoutComponent,
    MainLayoutComponent,
    LoginLayoutComponent,
  ],
  imports: [
    NotifierModule.withConfig(customNotifierOptions),
    BrowserModule,
    AppMaterialModule,
    HttpClientModule,
    CountdownModule,
    FontAwesomeModule,
    NgxMaskModule.forRoot({
      dropSpecialCharacters: true,
      clearIfNotMatch: true,
      patterns: {
        '0': { pattern: /\d/ },
        '9': { pattern: /\d/, optional: true },
        A: { pattern: /[a-zA-Z0-9]/ },
        S: { pattern: /[a-zA-Z]/ },
      },
    }),
    NgxCurrencyModule.forRoot(customCurrencyMaskConfig),
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    AppRoutingModule,
    LoginModule,
    AccountModule,
    SavedContactsModule,
    RegisterModule,
    OnboardingsModule,
    OrdersModule,
    AdminModule,
    BoobleModalModule,
    NoopAnimationsModule,
    BrowserAnimationsModule,
    SharedComponentsModule,
    RouterModule,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'pt-BR',
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ResponseInterceptor,
      multi: true,
    },
    {
      provide: MatPaginatorIntl,
      useClass: MatPaginatorIntlBr,
    },
    Title,
    WebsocketService,
    {
      provide: WebsocketService,
      //useFactory: rxStompServiceFactory,
    },
    // {
    //   provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
    //   useValue: { appearance: 'outline' },
    // },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
function ptBr(ptBr: any) {
  throw new Error('Function not implemented.');
}
